import groq from "groq";
import {
	getSanityPictureObject,
	getSanityImageSizesObject,
	getSanityMetaImageObject,
} from "@ishavskraft/shared";
import type { ImageWithMeta, SanityPicture } from "#lib/types/shared.js";
import type { AvtaleFeaturedDealItem } from "#lib/types/avtale.js";
import type { PageCover, PageBanner, PageGallery } from "#lib/types/page.js";

/**
 * Single source of truth for the shared "pageBuilder" block universe: the GROQ
 * projection that dereferences every block's images/references, and the JS pass
 * that resolves those images into responsive `<Picture>` objects.
 *
 * Used by every page-building surface — avtale, spotpris, historikk AND the
 * landingsside `[slug]` route — so a block behaves identically wherever it is
 * placed. Previously this logic was copy-pasted across spotprisenContent.ts,
 * historikkContent.ts and stromavtale/[slug]/+page.server.ts.
 */

export const STANDARD_IMAGE_SIZES: [number, number][] = [
	[700, 394],
	[900, 506],
	[1500, 844],
];

export const processImage = (
	image: ImageWithMeta | undefined | null
): ImageWithMeta | undefined => {
	if (!image) return undefined;
	const picture = getSanityPictureObject(image, { sizes: STANDARD_IMAGE_SIZES, fit: "crop" });
	return { ...image, ...(picture ?? {}) };
};

/**
 * Resolve an editor's SEO share image to the 1200x630 social crop that
 * Facebook and X expect, rather than `processImage`'s 16:9 content sizes.
 * `seoToMetaData` reads `src` plus the dimensions off the result.
 */
export const processSeoImage = (
	image: ImageWithMeta | undefined | null
): SanityPicture | undefined => {
	if (!image?.asset?._ref && !image?.asset?._id) return undefined;
	const meta = getSanityMetaImageObject(image);
	return { ...image, src: meta.image, width: meta.imageWidth, height: meta.imageHeight };
};

/**
 * Strip stega source-map markers from an enum-like string field. Sanity's
 * stega-enabled client wraps every string with invisible Unicode markers for
 * click-to-edit source mapping, which breaks JS equality on lookup fields like
 * `area`, `language` and `_type`. No-op when no markers are present.
 */
export const stripStega = (value: string): string =>
	value
		.replace(/[\uDB40][\uDC00-\uDCFF]/g, "") // Unicode Tags (stega)
		// eslint-disable-next-line no-misleading-character-class, no-irregular-whitespace
		.replace(/[​-‏︀-️﻿]/gu, "");

/**
 * Resolve every image/reference-carrying block into render-ready shape.
 * Accepts any `{ _type }` block so it works across every page-builder union
 * (ResolvedBlock, PageBuilderBlock, avtale's wider set) without call-site casts.
 */
export const processBlockImages = <T extends { _type: string }>(block: T): T => {
	const directImageFields: Record<string, string> = {
		personBanner: "image",
		interactiveModule: "sub_image",
		customerSupport: "image",
	};
	if (block._type in directImageFields) {
		const field = directImageFields[block._type];
		return {
			...block,
			[field]: processImage((block as unknown as Record<string, unknown>)[field] as ImageWithMeta),
		} as unknown as T;
	}

	if (block._type === "internalLinkingModule") {
		const module = (block as { module?: { image?: ImageWithMeta | null } }).module;
		if (!module) return block;
		return {
			...block,
			module: { ...module, image: processImage(module.image) ?? null },
		} as unknown as T;
	}

	if (block._type === "discountSection") {
		const discounts = (block as { discounts?: { image?: ImageWithMeta }[] }).discounts ?? [];
		return {
			...block,
			discounts: discounts.map((d) => ({ ...d, image: processImage(d.image) })),
		} as unknown as T;
	}

	if (block._type === "otherDeals") {
		const featuredAvtaler =
			(block as { featuredAvtaler?: AvtaleFeaturedDealItem[] }).featuredAvtaler ?? [];
		return {
			...block,
			featuredAvtaler: featuredAvtaler.map((avtale: AvtaleFeaturedDealItem) => ({
				...avtale,
				image: avtale.image
					? {
							...avtale.image,
							...(getSanityPictureObject(avtale.image as ImageWithMeta, {
								sizes: [
									[300, 80],
									[600, 160],
								],
								fit: "crop",
							}) ?? undefined),
						}
					: undefined,
			})),
		} as unknown as T;
	}

	// Landingsside blocks
	if (block._type === "cover") {
		const cover = (block as unknown as PageCover).cover;
		return {
			...block,
			cover: {
				...cover,
				image: {
					...cover.image,
					...getSanityImageSizesObject(cover.image?.asset?._id, {
						sizes: [[428], [856], [1199], [2398]],
					}),
				},
			},
		} as unknown as T;
	}

	if (block._type === "banner") {
		return {
			...block,
			image: processImage((block as unknown as PageBanner).image) ?? null,
		} as unknown as T;
	}

	if (block._type === "gallery") {
		const images = (block as unknown as PageGallery).images ?? [];
		return {
			...block,
			images: images.map((image: ImageWithMeta) => ({
				...image,
				...(getSanityPictureObject(image, { sizes: STANDARD_IMAGE_SIZES, fit: "crop" }) ?? {}),
			})),
		} as unknown as T;
	}

	return block;
};

/**
 * GROQ projection for a `pageBuilder` array member. Dereferences images and
 * references for every block type across all page-building document types.
 * Spread inside a `blocks[] { _key, _type, ${PAGE_BUILDER_BLOCK_PROJECTION} }`.
 */
export const PAGE_BUILDER_BLOCK_PROJECTION = groq`
  _type == "internalLinkingModule" => {
    ...,
    _type,
    module->{
      ...,
      image{ ..., hotspot, crop, asset->{ ..., metadata{dimensions} } }
    }
  },

  _type == "faqs" => {
    blockTitle,
    faqs[] { _key, question, answer[] { ... } }
  },

  _type == "info" => {
    title,
    description[]{
      ...,
      _type == "image" => { ..., asset-> }
    }
  },

  _type == "interactiveModule" => {
    title,
    sub_image { ..., asset-> },
    interactive_module_content[]{
      ...,
      _type == "image" => { ..., asset-> }
    },
    interactive_module_button,
    interactive_module_button_text,
    interactive_module_button_link
  },

	_type == "customerReviews" => {
		reviews[]{
			_key,
			...coalesce(@->, @){
				title,
				content[]{
					...,
					_type == "image" => { ..., asset-> }
				},
				stars,
				reviewDate,
				sourceText,
				source
			}
		}
	},
  _type == "discountSection" => {
    title,
    content[]{
      ...,
      _type == "image" => { ..., asset-> }
    },
    discounts[] {
      _key,
      title,
      description[]{
        ...,
        _type == "image" => { ..., asset-> }
      },
      image { ..., asset-> },
      linkText,
      link
    }
  },

  _type == "otherDeals" => {
    title,
    featuredAvtaler[] {
      _key,
      subtitle,
      image { ..., asset-> },
      reference-> { _id, title, slug, paaslag, fastbelop, perk }
    },
    avtaler[] {
      _key,
      subtitle,
      reference-> { _id, title, slug }
    }
  },

  _type == "perks" => {
    block_title,
    description[]{ ... },
    perks[] {
      _key,
      icon,
      title,
      description[]{ ... }
    }
  },

  _type == "personBanner" => {
    title,
    role,
    name,
    contact_email,
    contact_number,
    description[]{ ... },
    image { ..., asset-> }
  },

  _type == "customerSupport" => {
    "title": module->title,
    "description": module->description[]{
      ...,
      _type == "image" => { ..., asset-> }
    },
    "image": module->image { ..., asset-> },
    "label": module->label,
    "buttonText": module->buttonText
  },

  _type == "campaignBanner" => {
    title,
    description[]{ ... }
  },

  // Landingsside blocks
  _type == "cover" => {
    ...,
    _type,
    cover{
      title,
      image{ ..., asset->{ ..., metadata{dimensions} } }
    }
  },

  _type == "content" => {
    ...,
    content[]{
      ...,
      _type == "block" => {
        ...,
        children[]{
          ...,
          _type == "articleReference" => {
            ...,
            "title": @->.title,
            "id": @->._id,
            "slug": @->.slug.current
          },
          _type == "productReference" => {
            ...,
            "title": @->.title,
            "id": @->._id,
            "slug": @->.slug.current
          },
          _type == "fileReference" => {
            ...,
            "url": file.asset->url
          }
        }
      }
    }
  },

  _type == "gallery" => {
    ...,
    _type,
    images[]{ ..., hotspot, crop, asset->{ ..., metadata{dimensions} } }
  },

  _type == "pageFaqs" => {
    _type,
    faqs{
      heading,
      "items": items[]->{ _id, _type, question, answer }
    }
  },

  _type == "banner" => {
    ...,
    _type,
    content[]{
      ...,
      _type == "block" => {
        ...,
        children[]{
          ...,
          _type == "fileReference" => {
            ...,
            "url": file.asset->url
          }
        }
      }
    },
    image{ ..., hotspot, crop, asset->{ ..., metadata{dimensions} } }
  },

  _type == "customerServiceBanner" => {
    ...,
    _type
  }
`;
